// Centralized helpers to display checklist + templates in the user's current language.
// - For pre-seeded templates, we hardcode the EN mapping (see TEMPLATE_NAME_EN).
// - For checklist questions, we use the i18n cache stored in MongoDB at /api/checklist/translate.

// Preset color swatches shown in the template editor color picker. Kept in
// sync with backend DEFAULT_TEMPLATE_COLORS + FALLBACK_TEMPLATE_COLORS so
// managers see the same options that the seed data uses.
export const TEMPLATE_COLOR_SWATCHES = [
  "#1E3A8A", // navy (brand)
  "#F59E0B", // amber
  "#10B981", // emerald
  "#F43F5E", // rose
  "#8B5CF6", // violet
  "#0EA5E9", // sky
  "#64748B", // slate
  "#EA580C", // orange
  "#DB2777", // pink
  "#0D9488", // teal
  "#CA8A04", // gold
  "#DC2626", // red
];

// Fallback color when a template document is missing the color field.
export const TEMPLATE_COLOR_FALLBACK = "#94A3B8";  // stone/slate 400

// Resolves the display color for a template name against a `templates` list.
// Returns the fallback if the template can't be located.
export function templateColor(name, list) {
  if (!name || !Array.isArray(list)) return TEMPLATE_COLOR_FALLBACK;
  const t = list.find((x) => x?.name === name);
  return (t && t.color) || TEMPLATE_COLOR_FALLBACK;
}

export const TEMPLATE_NAME_EN = {
  Ventas: "Sales",
  Tienda: "Store",
  Imagen: "Image",
  Producto: "Product",
  Servicio: "Service",
  Gerente: "Manager",
  Limpieza: "Cleanliness",
};
export const TEMPLATE_NAME_ES = Object.fromEntries(
  Object.entries(TEMPLATE_NAME_EN).map(([k, v]) => [v, k]),
);

// Static EN dictionary for every category used by the pre-seeded Mueblería
// Canales checklist. Lets us translate tab labels even when we don't have the
// full question document at hand.
export const CATEGORY_EN = {
  "Imagen / Uniforme": "Image / Uniform",
  "Imagen / Identificación": "Image / ID Badge",
  "Herramientas": "Tools",
  "Seguimiento": "Follow-up",
  "Financiamiento": "Financing",
  "Servicio al cliente": "Customer Service",
  "Llamadas": "Calls",
  "Reputación": "Reputation",
  "Producto": "Product",
  "Conocimiento de producto": "Product Knowledge",
  "Recibimiento": "Greeting",
  "Señales de compra": "Buying Signals",
  "Preguntas clave": "Key Questions",
  "Personal": "Personnel",
  "Imagen": "Image",
  "Flujo de trabajo": "Workflow",
  "Relaciones": "Relationships",
  "Ventas": "Sales",
  "Servicio": "Service",
  "Equipo": "Team",
  "Motivación": "Motivation",
  "Software": "Software",
  "Operación": "Operations",
  "Metas": "Goals",
  "Transacciones": "Transactions",
  "Supervisión": "Supervision",
  "Entrenamiento": "Training",
  "Proveedores / Terceros": "Vendors / Third Parties",
  "Costos": "Costs",
  "Inventario": "Inventory",
  "Orden por áreas": "Area Ownership",
  "Catálogos": "Catalogs",
  "Flyers": "Flyers",
  "Inventario en piso": "Showroom Inventory",
  "Precios": "Prices",
  "Detalles de exhibición": "Display Details",
  "Publicidad financiera": "Financing Ads",
  "Imagen de tienda": "Store Image",
  "Seguridad": "Safety",
  "Material del vendedor": "Sales Rep Materials",
  "Promociones visibles": "Visible Promotions",
  "Experiencia del cliente": "Customer Experience",
  "Producto demostrable": "Demo-Ready Product",
  "Señales de interés": "Interest Signals",
  "Financiamiento en piso": "Showroom Financing",
  "Responsables": "Owners",
  "Auditoría diaria": "Daily Audit",
  "Corrección rápida": "Quick Fix",
  "Promociones": "Promotions",
  "Podium": "Podium",
  "Fechas": "Dates",
  "Cambios": "Changes",
  "Reviews": "Reviews",
  "Consistencia": "Consistency",
  "Material de ventas": "Sales Materials",
  "Publicidad en tienda": "In-Store Advertising",
  "Manager / Terceros": "Manager / Third Parties",
  "Comunicación interna": "Internal Communication",
  "Medición": "Measurement",
  "Cliente": "Customer",
  "Vencidos": "Expired",
  "Aprobación": "Approval",
  "Mejora": "Improvement",
  "POP": "POP",
};
export const CATEGORY_ES = Object.fromEntries(
  Object.entries(CATEGORY_EN).map(([k, v]) => [v, k]),
);

// Runtime map populated from the loaded checklist. Every category the user
// created (even outside the pre-seed) has both language variants cached in
// the checklist document — we mirror them here so `localizedCategory` can
// translate them anywhere in the app, regardless of which variant the caller
// passes in (primary, ES cache, or EN cache).
let RUNTIME_CATEGORY_LOOKUP = {}; // { anyKnownCategoryString: { es, en } }

export function registerChecklistI18n(items) {
  if (!Array.isArray(items)) return;
  const next = {};
  for (const q of items) {
    if (!q) continue;
    const primary = q.category || "";
    const catEs = ((q.i18n || {}).es || {}).category || primary;
    const catEn = ((q.i18n || {}).en || {}).category || primary;
    const pair = { es: catEs, en: catEn };
    if (primary) next[primary] = pair;
    if (catEs) next[catEs] = pair;
    if (catEn) next[catEn] = pair;
  }
  RUNTIME_CATEGORY_LOOKUP = next;
}

export function localizedCategory(name, lang) {
  if (!name) return "";
  const isEn = String(lang || "").startsWith("en");
  const target = isEn ? "en" : "es";
  // 1) Prefer live checklist cache (covers user-added categories that aren't in the static seed map).
  const hit = RUNTIME_CATEGORY_LOOKUP[name];
  if (hit && hit[target]) return hit[target];
  // 2) Fallback to the static seed map for pre-seeded categories.
  if (isEn) return CATEGORY_EN[name] || name;
  return CATEGORY_ES[name] || name;
}

export function localizedTemplate(name, lang) {
  if (!name) return "";
  if (String(lang || "").startsWith("en")) return TEMPLATE_NAME_EN[name] || name;
  return TEMPLATE_NAME_ES[name] || name;
}

export function localizedQuestion(q, lang) {
  if (!q) return { category: "", question: "" };
  const isEn = String(lang || "").startsWith("en");
  const target = isEn ? "en" : "es";
  // Always prefer the cached translation for the *active* language, whether
  // the primary text was authored in ES or EN. This fixes bilingual mixups
  // where user-added questions had English primary text but a valid ES cache.
  const cached = (q.i18n && q.i18n[target]) || {};
  return {
    category: cached.category || localizedCategory(q.category, lang),
    question: cached.question || q.question || "",
  };
}

export function localizedSeverity(value, lang) {
  const isEn = String(lang || "").startsWith("en");
  const map = isEn
    ? { Alta: "High", Media: "Medium", Baja: "Low" }
    : { High: "Alta", Medium: "Media", Low: "Baja" };
  return map[value] || value;
}
